/**
 * Other users' profiles, fetched through the getUserProfiles callable instead of
 * reading their users/{uid} documents directly. Rules plan S5a, 23 Sept 2026.
 *
 * Returns {uid, id, username, profile_picture}, plus email when the caller is
 * related to that person (shared project, affiliation, or an invitation), plus
 * hasOwnSubscription / institution_name inside an institution. Never the whole
 * record. See functions/lib/user-profiles.js for the rules.
 *
 * Every lookup made in the same tick goes out as ONE call (a dashboard of twenty
 * owners is one request), and answers are cached for a minute. On any failure the
 * answer is null, exactly as the old direct read behaved.
 */
import firebase from 'firebase/app';
import 'firebase/functions';

const TTL_MS = 60 * 1000;
const MAX_PER_CALL = 50;
const cache = new Map(); // uid -> { at, promise }
let queue = new Map(); // uid -> resolve
let timer = null;

function flush() {
    const batch = queue;
    queue = new Map();
    timer = null;
    const uids = Array.from(batch.keys());
    for (let i = 0; i < uids.length; i += MAX_PER_CALL) {
        const chunk = uids.slice(i, i + MAX_PER_CALL);
        firebase.functions().httpsCallable('getUserProfiles')({ uids: chunk })
            .then((res) => {
                const profiles = (res && res.data && res.data.profiles) || {};
                chunk.forEach(uid => batch.get(uid)(profiles[uid] || null));
            })
            .catch((e) => {
                console.error('[profiles] lookup failed:', e && e.message);
                chunk.forEach((uid) => {
                    cache.delete(uid);
                    batch.get(uid)(null);
                });
            });
    }
}

export default function profileByUID(uid) {
    if (!uid) return Promise.resolve(null);
    const hit = cache.get(uid);
    if (hit && Date.now() - hit.at < TTL_MS) return hit.promise;
    const promise = new Promise((resolve) => { queue.set(uid, resolve); });
    cache.set(uid, { at: Date.now(), promise });
    if (!timer) timer = setTimeout(flush, 0);
    return promise;
}

/**
 * Another member's settings for ONE project you are both on (users/{uid}.
 * projects[pid]), via getPeerProjectSettings. Rules plan S5b. Used by the
 * bookmark merge and by duplicating a project you do not own. Resolves to {}
 * when they have none, null if the lookup fails or you are not both members.
 */
export function peerProjectSettings(uid, pid) {
    if (!uid || !pid) return Promise.resolve(null);
    return firebase.functions().httpsCallable('getPeerProjectSettings')({ uid, pid })
        .then(res => (res && res.data && res.data.settings) || null)
        .catch((e) => {
            console.error('[profiles] peer settings failed:', e && e.message);
            return null;
        });
}
/**
 * Which members of ONE project have Observer accounts, plus the owner's plan
 * family, via getProjectAccessInfo (1 Oct 2026). Booleans only, and only for a
 * member of the project. Not cached: payment changes, and the dialog asks once
 * per open. Resolves null on any failure, which restricts nothing (old behaviour).
 */
export function projectAccessInfo(pid) {
    if (!pid) return Promise.resolve(null);
    return firebase.functions().httpsCallable('getProjectAccessInfo')({ pid })
        .then(res => (res && res.data) || null)
        .catch((e) => {
            console.error('[profiles] project access info failed:', e && e.message);
            return null;
        });
}



// WEBPACK FOOTER //
// ./src/lib/user-profiles.js